import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import '@css/PopUp.css';

/**
 * BaseDialog - Reusable dialog component using HTML5 <dialog> element
 *
 * Features:
 * - Opens with show(), not showModal(): a modal dialog would make the toast
 *   container inert and paint it under ::backdrop. PopUp.css draws the dimmed
 *   overlay, and Escape is handled on document.
 * - ESC key and backdrop click handling
 * - Proper cleanup and accessibility
 * - Flexible content via children prop
 * - Optional header, footer, and action buttons
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls dialog visibility
 * @param {function} props.onClose - Callback when dialog should close
 * @param {React.ReactNode} props.children - Main content of the dialog
 * @param {string} [props.title] - Optional dialog title
 * @param {React.ReactNode} [props.footer] - Optional footer content
 * @param {string} [props.className] - Additional CSS class for dialog element
 * @param {string} [props.contentClassName] - Additional CSS class for content wrapper
 * @param {boolean} [props.closeOnBackdrop=true] - Whether clicking backdrop closes dialog
 * @param {boolean} [props.closeOnEsc=true] - Whether ESC key closes dialog
 */
const BaseDialog = ({
    isOpen,
    onClose,
    children,
    title,
    footer,
    className = '',
    contentClassName = '',
    closeOnBackdrop = true,
    closeOnEsc = true,
}) => {
    const dialogRef = useRef(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) {
            return;
        }

        let backdropPressed = false;

        if (isOpen) {
            if (!dialog.open) {
                dialog.show();
            }
        } else if (dialog.open) {
            dialog.close();
        }

        const handleBackdropMouseDown = (e) => {
            backdropPressed = e.target === dialog;
        };

        const handleBackdropClick = (e) => {
            const shouldClose = closeOnBackdrop && backdropPressed && e.target === dialog;
            backdropPressed = false;

            if (shouldClose) {
                onClose();
            }
        };

        const handleCancel = (e) => {
            if (closeOnEsc) {
                e.preventDefault();
                onClose();
            } else {
                e.preventDefault();
            }
        };

        // A non-modal dialog gets no native "cancel" on Escape
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && closeOnEsc && dialog.open) {
                onClose();
            }
        };

        dialog.addEventListener('mousedown', handleBackdropMouseDown);
        dialog.addEventListener('click', handleBackdropClick);
        dialog.addEventListener('cancel', handleCancel);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            dialog.removeEventListener('mousedown', handleBackdropMouseDown);
            dialog.removeEventListener('click', handleBackdropClick);
            dialog.removeEventListener('cancel', handleCancel);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, closeOnBackdrop, closeOnEsc]);

    if (!isOpen) {
        return null;
    }

    return (
        <dialog ref={dialogRef} className={`popup ${className}`.trim()}>
            <div className={`popup-content ${contentClassName}`.trim()}>
                {title ? <h2>{title}</h2> : null}
                
                <div className="dialog-body">
                    {children}
                </div>

                {footer ? <div className="dialog-footer">
                    {footer}
                </div> : null}
            </div>
        </dialog>
    );
};

BaseDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    title: PropTypes.string,
    footer: PropTypes.node,
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    closeOnBackdrop: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
};

export default BaseDialog;
